import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlvoLogo } from '../components/AlvoLogo';
import { Button } from '../components/ui/button';
import { 
  Target, 
  TrendingUp, 
  BarChart3, 
  PieChart, 
  Zap,
  Shield,
  Clock,
  CheckCircle,
  ArrowRight,
  X,
  ChevronDown,
  LineChart,
  Wallet,
  Goal,
  FileText,
  Users,
  Building2,
  Star,
  Quote,
  Brain,
  AlertTriangle,
  Lightbulb,
  DollarSign,
  TrendingDown,
  Sparkles,
  MessageSquare,
  Activity,
  Phone
} from 'lucide-react';

const PRIMARY_COLOR = '#22C55E';
const SECONDARY_COLOR = '#10B981';
const GRADIENT = `linear-gradient(135deg, ${PRIMARY_COLOR}, ${SECONDARY_COLOR})`;
const WHATSAPP_NUMBER = '5584999683171';
const WHATSAPP_URL = `https://wa.me/${WHATSAPP_NUMBER}?text=Olá! Gostaria de saber mais sobre o ALVO.`;

// Animated counter component
const AnimatedCounter = ({ end, duration = 2000, prefix = '', suffix = '' }) => {
  const [count, setCount] = useState(0);
  
  useEffect(() => {
    let startTime;
    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      setCount(Math.floor(progress * end));
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [end, duration]);
  
  return <span>{prefix}{count.toLocaleString('pt-BR')}{suffix}</span>;
};

// FAQ Accordion Item
const FAQItem = ({ question, answer, isOpen, onClick }) => (
  <div className="border-b border-white/10">
    <button
      onClick={onClick}
      className="w-full py-6 flex items-center justify-between text-left group"
      data-testid={`faq-item-${question.slice(0, 20).replace(/\s/g, '-').toLowerCase()}`}
    >
      <span className="text-lg font-medium text-white group-hover:text-[#22C55E] transition-colors pr-4">
        {question}
      </span>
      <ChevronDown 
        className={`w-5 h-5 text-zinc-400 transition-transform duration-300 flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} 
      />
    </button>
    <div className={`overflow-hidden transition-all duration-300 ${isOpen ? 'max-h-96 pb-6' : 'max-h-0'}`}>
      <p className="text-zinc-400 leading-relaxed">{answer}</p>
    </div>
  </div>
);

// Feature Card Component
const FeatureCard = ({ icon: Icon, title, description, delay = 0 }) => (
  <div 
    className="relative group p-6 rounded-2xl bg-zinc-900/50 border border-white/5 hover:border-[#22C55E]/30 transition-all duration-500"
    style={{ animationDelay: `${delay}ms` }}
  >
    <div 
      className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
      style={{ background: `${PRIMARY_COLOR}15` }}
    >
      <Icon className="w-6 h-6" style={{ color: PRIMARY_COLOR }} />
    </div>
    <h3 className="text-xl font-semibold text-white mb-2">{title}</h3>
    <p className="text-zinc-400 leading-relaxed">{description}</p>
    
    {/* Hover glow effect */}
    <div 
      className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
      style={{ 
        background: `radial-gradient(circle at center, ${PRIMARY_COLOR}05 0%, transparent 70%)`,
        filter: 'blur(20px)'
      }}
    />
  </div>
);

// Testimonial Card
const TestimonialCard = ({ name, role, company, text }) => (
  <div className="p-6 rounded-2xl bg-zinc-900/50 border border-white/5 relative">
    <Quote className="w-8 h-8 text-[#22C55E]/20 absolute top-4 right-4" />
    <p className="text-zinc-300 leading-relaxed mb-6 italic">"{text}"</p>
    <div className="flex items-center gap-3">
      <div 
        className="w-12 h-12 rounded-full bg-gradient-to-br from-[#22C55E] to-[#10B981] flex items-center justify-center text-black font-bold text-lg"
      >
        {name.charAt(0)}
      </div>
      <div>
        <p className="font-semibold text-white">{name}</p>
        <p className="text-sm text-zinc-500">{role} - {company}</p>
      </div>
    </div>
  </div>
);

// AI Analysis Card Component - Shows mockup of AI analysis
const AIAnalysisCard = ({ title, icon: Icon, items, color }) => (
  <div className="p-4 rounded-xl bg-black/40 border border-white/5">
    <div className="flex items-center gap-2 mb-3">
      <Icon className="w-5 h-5" style={{ color }} />
      <span className="text-sm font-semibold" style={{ color }}>{title}</span>
    </div>
    <ul className="space-y-2">
      {items.map((item, i) => (
        <li key={item} className="flex items-start gap-2 text-sm text-zinc-400">
          <span className="text-zinc-600">•</span>
          <span>{item}</span>
        </li>
      ))}
    </ul>
  </div>
);

export const LandingPage = () => {
  const navigate = useNavigate();
  const [openFAQ, setOpenFAQ] = useState(null);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const faqs = [
    {
      question: "O Diretor Financeiro com IA substitui meu contador?",
      answer: "Não. O Diretor Financeiro com IA é uma ferramenta de análise estratégica que complementa o trabalho do seu contador. Enquanto ele cuida da parte fiscal e contábil, a IA analisa seus dados e te mostra riscos, oportunidades e recomendações práticas para aumentar seu lucro."
    },
    {
      question: "Como a IA sabe do meu tipo de negócio?",
      answer: "Ao cadastrar sua empresa, você informa o setor (restaurante, academia, clínica, etc.) e uma descrição do seu negócio. A IA usa essas informações para personalizar 100% da análise, usando benchmarks específicos do seu setor e recomendações que fazem sentido para o seu modelo."
    },
    {
      question: "Preciso de conhecimento técnico para usar?",
      answer: "Absolutamente não. O Alvo foi projetado para empresários e gestores, não para especialistas em tecnologia. A interface é intuitiva e a IA te entrega análises em linguagem clara e direta, como se fosse uma conversa com um CFO experiente."
    },
    {
      question: "Com que frequência recebo análises da IA?",
      answer: "Você pode gerar análises a qualquer momento. O ideal é fazer um 'fechamento de mês' mensal, onde a IA analisa todos os dados do período e te entrega um diagnóstico completo com plano de ação."
    },
    {
      question: "Posso gerenciar mais de uma empresa?",
      answer: "Sim! O Alvo suporta múltiplas unidades de negócio. Você pode gerenciar todas as suas empresas em um único lugar, com análises de IA separadas para cada uma."
    },
    {
      question: "Posso cancelar a qualquer momento?",
      answer: "Sim. Não há fidelidade. Você pode cancelar sua assinatura quando quiser, sem multas ou taxas adicionais."
    }
  ];

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-[#030303] text-white overflow-x-hidden">
      {/* Floating Header */}
      <header 
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-[#030303]/90 backdrop-blur-xl border-b border-white/5' : ''
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <AlvoLogo size={40} />
            <span className="font-bold text-lg tracking-tight hidden sm:block">ALVO</span>
          </div>
          
          <nav className="hidden md:flex items-center gap-8">
            <button 
              onClick={() => scrollToSection('ai-director')}
              className="text-zinc-400 hover:text-white transition-colors"
            >
              Diretor Financeiro IA
            </button>
            <button 
              onClick={() => scrollToSection('features')}
              className="text-zinc-400 hover:text-white transition-colors"
            >
              Funcionalidades
            </button>
            <button 
              onClick={() => scrollToSection('pricing')}
              className="text-zinc-400 hover:text-white transition-colors"
            >
              Preços
            </button>
            <button 
              onClick={() => scrollToSection('faq')}
              className="text-zinc-400 hover:text-white transition-colors"
            >
              FAQ
            </button>
          </nav>

          <div className="flex items-center gap-3">
            <Button
              onClick={() => navigate('/login?tab=login')}
              variant="ghost"
              className="text-zinc-300 hover:text-white"
              data-testid="header-login-btn"
            >
              Entrar
            </Button>
            <Button
              onClick={() => navigate('/login?tab=register')}
              className="text-black font-semibold px-6"
              style={{ background: GRADIENT }}
              data-testid="header-cta-btn"
            >
              Começar Agora
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section - NEW DESIGN */}
      <section className="relative min-h-screen flex items-center pt-20">
        {/* Background effects */}
        <div className="absolute inset-0 overflow-hidden">
          {/* Gradiente verde principal */}
          <div 
            className="absolute top-1/4 -left-1/4 w-[600px] h-[600px] rounded-full blur-[120px] opacity-20"
            style={{ background: PRIMARY_COLOR }}
          />
          {/* Gradiente secundário */}
          <div 
            className="absolute bottom-1/4 -right-1/4 w-[500px] h-[500px] rounded-full blur-[100px] opacity-15"
            style={{ background: SECONDARY_COLOR }}
          />
          {/* Grid pattern - SUTIL */}
          <div 
            className="absolute inset-0 opacity-[0.03]"
            style={{
              backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
              backgroundSize: '50px 50px'
            }}
          />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left content */}
            <div className="text-center lg:text-left">
              {/* Badge */}
              <div 
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm mb-8 animate-fade-in-up"
                style={{ 
                  background: `${PRIMARY_COLOR}10`,
                  border: `1px solid ${PRIMARY_COLOR}30`
                }}
              >
                <Brain className="w-4 h-4" style={{ color: PRIMARY_COLOR }} />
                <span className="text-zinc-300">Inteligência Artificial Financeira</span>
              </div>

              {/* Pre-headline */}
              <p className="text-lg sm:text-xl mb-4 animate-fade-in-up" style={{ animationDelay: '50ms', color: '#A1A1AA' }}>
                O ALVO não é apenas um sistema.
              </p>

              {/* Main headline */}
              <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold leading-[1.15] mb-6 animate-fade-in-up" style={{ animationDelay: '100ms' }}>
                <span style={{ color: '#FFFFFF' }}>E se sua empresa tivesse um </span>
                <span style={{ 
                  background: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}>
                  Diretor Financeiro?
                </span>
              </h1>

              {/* Subheadline - Benefits list */}
              <div className="text-lg mb-6 animate-fade-in-up" style={{ animationDelay: '200ms', color: '#A1A1AA' }}>
                <p className="mb-4">Alguém analisando seus números todos os meses.</p>
                <p className="mb-3">Mostrando:</p>
                <ul className="space-y-2 text-left max-w-md mx-auto lg:mx-0">
                  <li className="flex items-center gap-3">
                    <AlertTriangle className="w-5 h-5 flex-shrink-0" style={{ color: '#F87171' }} />
                    <span><strong style={{ color: '#F87171' }}>Riscos</strong> <span style={{ color: '#A1A1AA' }}>que você não está vendo</span></span>
                  </li>
                  <li className="flex items-center gap-3">
                    <Lightbulb className="w-5 h-5 flex-shrink-0" style={{ color: '#FACC15' }} />
                    <span><strong style={{ color: '#FACC15' }}>Oportunidades</strong> <span style={{ color: '#A1A1AA' }}>de ganhar mais</span></span>
                  </li>
                  <li className="flex items-center gap-3">
                    <DollarSign className="w-5 h-5 flex-shrink-0" style={{ color: '#4ADE80' }} />
                    <span><strong style={{ color: '#4ADE80' }}>Lucro real</strong> <span style={{ color: '#A1A1AA' }}>do seu negócio</span></span>
                  </li>
                </ul>
              </div>

              {/* Power statement */}
              <p className="text-xl sm:text-2xl font-semibold mb-8 animate-fade-in-up" style={{ animationDelay: '250ms', color: '#22C55E' }}>
                Agora você tem.
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start animate-fade-in-up" style={{ animationDelay: '300ms' }}>
                <Button
                  onClick={() => navigate('/login?tab=register')}
                  className="h-14 px-8 text-lg text-black font-bold"
                  style={{ background: GRADIENT }}
                  data-testid="hero-cta-primary"
                >
                  Conhecer o ALVO
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <Button
                  onClick={() => scrollToSection('ai-director')}
                  variant="outline"
                  className="h-14 px-8 text-lg border-zinc-700 text-zinc-300 hover:bg-white/5 hover:text-white"
                  data-testid="hero-cta-secondary"
                >
                  <Brain className="mr-2 w-5 h-5" />
                  Ver IA em Ação
                </Button>
              </div>

              {/* Social proof mini */}
              <div className="flex items-center gap-6 mt-10 justify-center lg:justify-start animate-fade-in-up" style={{ animationDelay: '400ms' }}>
                <div className="flex -space-x-3">
                  {['M', 'R', 'C', 'J'].map((letter, i) => (
                    <div
                      key={letter}
                      className="w-10 h-10 rounded-full border-2 border-[#030303] flex items-center justify-center text-sm font-bold"
                      style={{ 
                        background: i % 2 === 0 ? PRIMARY_COLOR : SECONDARY_COLOR,
                        color: '#000'
                      }}
                    >
                      {letter}
                    </div>
                  ))}
                </div>
                <div className="text-left">
                  <div className="flex items-center gap-1">
                    {[1,2,3,4,5].map(i => (
                      <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <p className="text-sm text-zinc-500">+500 empresários confiam no ALVO</p>
                </div>
              </div>
            </div>

            {/* Right - AI Director Preview */}
            <div className="relative animate-fade-in-up" style={{ animationDelay: '300ms' }}>
              {/* Glow behind */}
              <div 
                className="absolute -inset-4 rounded-3xl blur-3xl opacity-30"
                style={{ background: GRADIENT }}
              />
              
              {/* AI Director mockup */}
              <div className="relative rounded-2xl border border-white/10 bg-zinc-900/80 backdrop-blur-xl p-4 shadow-2xl">
                {/* Header */}
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-3 h-3 rounded-full bg-red-500" />
                  <div className="w-3 h-3 rounded-full bg-yellow-500" />
                  <div className="w-3 h-3 rounded-full bg-green-500" />
                  <span className="text-xs text-zinc-500 ml-2">diretor-financeiro.alvo.app</span>
                </div>
                
                {/* AI Analysis Header */}
                <div className="flex items-center gap-3 mb-4 p-3 rounded-xl bg-gradient-to-r from-[#22C55E]/10 to-[#10B981]/10 border border-[#22C55E]/20">
                  <div className="p-2 rounded-lg bg-gradient-to-br from-[#22C55E] to-[#10B981]">
                    <Brain className="w-5 h-5 text-black" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-white">Análise de Dezembro/2025</p>
                    <p className="text-xs text-zinc-400">Restaurante Sabor da Casa</p>
                  </div>
                </div>

                {/* Mock Financial Cards */}
                <div className="grid grid-cols-3 gap-2 mb-4">
                  <div className="p-2 rounded-lg bg-black/40 border border-white/5 text-center">
                    <p className="text-xs text-zinc-500">Receita</p>
                    <p className="text-sm font-bold text-[#22C55E]">R$ 185k</p>
                  </div>
                  <div className="p-2 rounded-lg bg-black/40 border border-white/5 text-center">
                    <p className="text-xs text-zinc-500">Despesas</p>
                    <p className="text-sm font-bold text-red-400">R$ 142k</p>
                  </div>
                  <div className="p-2 rounded-lg bg-black/40 border border-white/5 text-center">
                    <p className="text-xs text-zinc-500">Lucro</p>
                    <p className="text-sm font-bold text-[#10B981]">R$ 43k</p>
                  </div>
                </div>

                {/* Mock AI Analysis Sections */}
                <div className="space-y-3">
                  <AIAnalysisCard 
                    title="RADAR DE PROBLEMAS"
                    icon={AlertTriangle}
                    color="#ef4444"
                    items={[
                      "CMV acima do benchmark (38% vs 32%)",
                      "Desperdício estimado: R$ 8.500/mês"
                    ]}
                  />
                  <AIAnalysisCard 
                    title="OPORTUNIDADES"
                    icon={Lightbulb}
                    color="#eab308"
                    items={[
                      "Buffet noturno pode adicionar R$ 25k/mês",
                      "Ocupação atual: 65% → potencial 85%"
                    ]}
                  />
                  <AIAnalysisCard 
                    title="PLANO DE AÇÃO"
                    icon={Target}
                    color={PRIMARY_COLOR}
                    items={[
                      "Renegociar fornecedores: economia R$ 3k",
                      "Implementar programa de fidelidade"
                    ]}
                  />
                </div>
              </div>
              
              {/* Floating badges */}
              <div 
                className="absolute -top-4 -right-4 p-3 rounded-xl bg-zinc-900 border border-white/10 shadow-lg animate-float-slow"
              >
                <div className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-[#22C55E]" />
                  <span className="text-sm font-medium">IA Personalizada</span>
                </div>
              </div>
              
              <div 
                className="absolute -bottom-4 -left-4 p-3 rounded-xl bg-zinc-900 border border-white/10 shadow-lg animate-float-reverse"
              >
                <div className="flex items-center gap-2">
                  <Brain className="w-5 h-5 text-[#10B981]" />
                  <span className="text-sm font-medium">Análise Mensal</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <ChevronDown className="w-6 h-6 text-zinc-600" />
        </div>
      </section>

      {/* AI Director Section - MAIN DIFFERENTIATOR */}
      <section id="ai-director" className="py-20 lg:py-32 relative">
        <div 
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full blur-[150px] opacity-10"
          style={{ background: GRADIENT }}
        />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <span 
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm mb-6"
              style={{ background: `${PRIMARY_COLOR}15`, color: PRIMARY_COLOR }}
            >
              <Brain className="w-4 h-4" />
              Diretor Financeiro com I.A.
            </span>
            
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6">
              Tenha um diretor financeiro analisando<br />
              <span style={{ color: PRIMARY_COLOR }}>sua empresa todos os meses</span>
            </h2>
            
            <p className="text-lg text-zinc-400 max-w-3xl mx-auto">
              Veja a saúde real da sua empresa em segundos. A IA analisa seus números, 
              identifica problemas ocultos, encontra oportunidades e te entrega um plano de ação prático.
            </p>
          </div>

          {/* AI Analysis Demo */}
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left - What AI Does */}
            <div className="space-y-6">
              <h3 className="text-2xl font-bold mb-6">O que seu Diretor Financeiro faz por você:</h3>
              
              {[
                {
                  icon: AlertTriangle,
                  color: '#ef4444',
                  title: 'Detecta Riscos',
                  desc: 'Identifica problemas financeiros antes que se tornem crises. Margem baixa? Despesas crescendo? A IA avisa.'
                },
                {
                  icon: Lightbulb,
                  color: '#eab308',
                  title: 'Encontra Oportunidades',
                  desc: 'Mostra onde você está deixando dinheiro na mesa. Calcula exatamente quanto pode ganhar a mais.'
                },
                {
                  icon: Activity,
                  color: SECONDARY_COLOR,
                  title: 'Simula Decisões',
                  desc: '"Se você fizer X, seu lucro aumenta Y". Simulações concretas com números reais do seu negócio.'
                },
                {
                  icon: Target,
                  color: PRIMARY_COLOR,
                  title: 'Plano de Ação Expert',
                  desc: 'Não apenas diz O QUE fazer, mas COMO fazer. Passo a passo, scripts, prazos e métricas de sucesso.'
                }
              ].map((item, i) => (
                <div key={item.title} className="flex items-start gap-4 p-4 rounded-xl bg-zinc-900/50 border border-white/5">
                  <div 
                    className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ background: `${item.color}15` }}
                  >
                    <item.icon className="w-6 h-6" style={{ color: item.color }} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-white mb-1">{item.title}</h4>
                    <p className="text-zinc-400 text-sm">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Right - Full AI Analysis Mockup */}
            <div className="relative">
              <div 
                className="absolute -inset-4 rounded-3xl blur-3xl opacity-20"
                style={{ background: GRADIENT }}
              />
              
              <div className="relative rounded-2xl border border-white/10 bg-zinc-900/80 backdrop-blur-xl p-6 shadow-2xl">
                {/* Browser header */}
                <div className="flex items-center gap-2 mb-4 pb-4 border-b border-white/5">
                  <div className="w-3 h-3 rounded-full bg-red-500" />
                  <div className="w-3 h-3 rounded-full bg-yellow-500" />
                  <div className="w-3 h-3 rounded-full bg-green-500" />
                  <span className="text-xs text-zinc-500 ml-2">alvo.app/diretor-financeiro</span>
                </div>

                {/* Analysis Content */}
                <div className="space-y-4">
                  {/* Header */}
                  <div className="flex items-center gap-3 p-3 rounded-xl bg-gradient-to-r from-[#22C55E]/10 to-[#10B981]/10 border border-[#22C55E]/20">
                    <div className="p-2.5 rounded-xl bg-gradient-to-br from-[#22C55E] to-[#10B981]">
                      <Brain className="w-6 h-6 text-black" />
                    </div>
                    <div>
                      <p className="font-bold text-white">Análise de Dezembro/2025</p>
                      <p className="text-sm text-zinc-400">Academia Fitness Pro • 200 alunos</p>
                    </div>
                  </div>

                  {/* Diagnosis */}
                  <div className="p-4 rounded-xl bg-black/40 border border-white/5">
                    <h4 className="text-sm font-bold text-[#22C55E] mb-2 flex items-center gap-2">
                      <Target className="w-4 h-4" /> DIAGNÓSTICO DIRETO
                    </h4>
                    <p className="text-sm text-zinc-300 leading-relaxed">
                      Seu negócio está <strong className="text-yellow-400">em atenção</strong>. 
                      A folha de pagamento está consumindo 42% do faturamento — 
                      acima do benchmark de 35% para academias. 
                      A boa notícia: seu ticket médio está excelente e há espaço para crescer.
                    </p>
                  </div>

                  {/* Problems */}
                  <div className="p-4 rounded-xl bg-red-500/5 border border-red-500/20">
                    <h4 className="text-sm font-bold text-red-400 mb-2 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" /> RADAR DE PROBLEMAS
                    </h4>
                    <ul className="space-y-1 text-sm text-zinc-300">
                      <li className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-red-400" />
                        Folha 42% do faturamento (máximo: 35%)
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-yellow-400" />
                        Energia cresceu 18% vs mês anterior
                      </li>
                    </ul>
                  </div>

                  {/* Simulation */}
                  <div className="p-4 rounded-xl bg-[#10B981]/5 border border-[#10B981]/20">
                    <h4 className="text-sm font-bold text-[#10B981] mb-2 flex items-center gap-2">
                      <TrendingUp className="w-4 h-4" /> SIMULAÇÃO
                    </h4>
                    <p className="text-sm text-zinc-300">
                      Se aumentar ticket médio em <strong className="text-white">R$ 30/aluno</strong>:
                    </p>
                    <p className="text-lg font-bold text-[#22C55E] mt-1">
                      +R$ 6.000/mês • +R$ 72.000/ano
                    </p>
                  </div>

                  {/* Action Plan */}
                  <div className="p-4 rounded-xl bg-[#22C55E]/5 border border-[#22C55E]/20">
                    <h4 className="text-sm font-bold text-[#22C55E] mb-2 flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" /> PLANO DE AÇÃO
                    </h4>
                    <div className="text-sm text-zinc-300">
                      <p className="font-medium text-white">1. Campanha de indicação</p>
                      <p className="text-xs text-zinc-400 mt-1">
                        COMO: Ofereça 1 mês grátis por indicação. Meta: 20 novos alunos/mês.
                        <br />Potencial: <span className="text-[#22C55E]">+R$ 9.700/mês</span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Dashboard + Analytics Section */}
      <section id="features" className="py-20 lg:py-32 bg-zinc-900/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span 
              className="inline-block px-3 py-1 rounded-full text-sm mb-4"
              style={{ background: `${SECONDARY_COLOR}15`, color: SECONDARY_COLOR }}
            >
              Gestão Completa
            </span>
            
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6">
              Dashboard analítico + <span style={{ color: PRIMARY_COLOR }}>IA estratégica</span>
            </h2>
            
            <p className="text-lg text-zinc-400 max-w-3xl mx-auto">
              Além do Diretor Financeiro com IA, você tem um painel completo para acompanhar 
              todos os indicadores do seu negócio em tempo real.
            </p>
          </div>

          {/* Dashboard Showcase */}
          <div className="mb-16">
            <div className="relative">
              <div 
                className="absolute -inset-4 rounded-3xl blur-3xl opacity-20"
                style={{ background: GRADIENT }}
              />
              
              <div className="relative rounded-2xl border border-white/10 bg-zinc-900/80 backdrop-blur-xl p-6 shadow-2xl overflow-hidden">
                {/* Browser header */}
                <div className="flex items-center gap-2 mb-6 pb-4 border-b border-white/5">
                  <div className="w-3 h-3 rounded-full bg-red-500" />
                  <div className="w-3 h-3 rounded-full bg-yellow-500" />
                  <div className="w-3 h-3 rounded-full bg-green-500" />
                  <span className="text-xs text-zinc-500 ml-3">alvo.app/dashboard</span>
                </div>

                {/* Dashboard mockup */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
                  {/* Sidebar */}
                  <div className="hidden lg:block lg:col-span-2 space-y-2">
                    <div className="flex items-center gap-2 p-3 rounded-lg bg-[#22C55E]/10 border border-[#22C55E]/20">
                      <BarChart3 className="w-4 h-4 text-[#22C55E]" />
                      <span className="text-sm text-[#22C55E]">Dashboard</span>
                    </div>
                    {[
                      { icon: Brain, label: 'Diretor I.A.' },
                      { icon: DollarSign, label: 'Receitas' },
                      { icon: TrendingDown, label: 'Despesas' },
                      { icon: Target, label: 'Metas' },
                      { icon: TrendingUp, label: 'ROI' },
                      { icon: FileText, label: 'Relatórios' }
                    ].map((item, i) => (
                      <div key={item.label} className="flex items-center gap-2 p-3 rounded-lg hover:bg-white/5 transition-colors">
                        <item.icon className="w-4 h-4 text-zinc-500" />
                        <span className="text-sm text-zinc-400">{item.label}</span>
                      </div>
                    ))}
                  </div>

                  {/* Main content */}
                  <div className="col-span-1 lg:col-span-10 space-y-4">
                    {/* Stats cards */}
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                      <div className="p-3 lg:p-4 rounded-xl bg-black/40 border border-white/5">
                        <p className="text-xs text-zinc-500 mb-1">Receita Total</p>
                        <p className="text-lg lg:text-2xl font-bold text-[#22C55E]">R$ 287.5k</p>
                        <p className="text-xs text-green-400 mt-1">+12.5% vs mês anterior</p>
                      </div>
                      <div className="p-3 lg:p-4 rounded-xl bg-black/40 border border-white/5">
                        <p className="text-xs text-zinc-500 mb-1">Despesas</p>
                        <p className="text-lg lg:text-2xl font-bold text-red-400">R$ 142.3k</p>
                        <p className="text-xs text-red-400 mt-1">+3.2% vs mês anterior</p>
                      </div>
                      <div className="p-3 lg:p-4 rounded-xl bg-black/40 border border-white/5">
                        <p className="text-xs text-zinc-500 mb-1">Lucro Líquido</p>
                        <p className="text-lg lg:text-2xl font-bold text-[#10B981]">R$ 145.2k</p>
                        <p className="text-xs text-green-400 mt-1">+18.7% vs mês anterior</p>
                      </div>
                      <div className="p-3 lg:p-4 rounded-xl bg-black/40 border border-white/5">
                        <p className="text-xs text-zinc-500 mb-1">ROI Mensal</p>
                        <p className="text-lg lg:text-2xl font-bold text-yellow-400">34.2%</p>
                        <p className="text-xs text-green-400 mt-1">Meta: 30%</p>
                      </div>
                    </div>

                    {/* Charts row */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                      {/* Main chart */}
                      <div className="lg:col-span-2 p-4 rounded-xl bg-black/40 border border-white/5">
                        <div className="flex items-center justify-between mb-4">
                          <p className="text-sm font-medium">Evolução Financeira</p>
                          <div className="flex items-center gap-4 text-xs">
                            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#22C55E]"></span> Receita</span>
                            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-400"></span> Despesa</span>
                          </div>
                        </div>
                        <div className="h-24 lg:h-32 flex items-end gap-2">
                          {[60, 70, 55, 80, 75, 90, 85, 95, 88, 100, 92, 98].map((h, i) => (
                            <div 
                              key={i}
                              className="flex-1 rounded-t"
                              style={{ height: `${h}%`, background: i === 11 ? GRADIENT : `${PRIMARY_COLOR}40` }}
                            />
                          ))}
                        </div>
                      </div>

                      {/* Goals */}
                      <div className="p-4 rounded-xl bg-black/40 border border-white/5">
                        <p className="text-sm font-medium mb-4">Metas do Mês</p>
                        <div className="space-y-3">
                          {[
                            { name: 'Faturamento', percent: 96 },
                            { name: 'Lucro', percent: 97 },
                            { name: 'ROI', percent: 114 }
                          ].map((goal, i) => (
                            <div key={goal.name}>
                              <div className="flex justify-between text-xs mb-1">
                                <span className="text-zinc-400">{goal.name}</span>
                                <span className={goal.percent >= 100 ? 'text-[#22C55E]' : 'text-[#10B981]'}>{goal.percent}%</span>
                              </div>
                              <div className="h-2 rounded-full bg-zinc-800 overflow-hidden">
                                <div 
                                  className="h-full rounded-full"
                                  style={{ 
                                    width: `${Math.min(goal.percent, 100)}%`,
                                    background: goal.percent >= 100 ? PRIMARY_COLOR : SECONDARY_COLOR
                                  }}
                                />
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Feature Cards */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <FeatureCard 
              icon={Brain}
              title="Diretor Financeiro IA"
              description="Análise estratégica mensal com diagnóstico, riscos, oportunidades e plano de ação personalizado para seu tipo de negócio."
              delay={0}
            />
            <FeatureCard 
              icon={BarChart3}
              title="Dashboard Inteligente"
              description="Visualize receitas, despesas e lucro em tempo real. Gráficos modernos que mostram a saúde financeira instantaneamente."
              delay={100}
            />
            <FeatureCard 
              icon={Target}
              title="Gestão de Metas"
              description="Defina objetivos financeiros e acompanhe o progresso automaticamente. O sistema calcula seu avanço com dados reais."
              delay={200}
            />
            <FeatureCard 
              icon={TrendingUp}
              title="Cálculo de ROI"
              description="Saiba exatamente o retorno dos seus investimentos. Fórmula precisa: (Lucro Mensal / Capital Investido) × 100."
              delay={300}
            />
            <FeatureCard 
              icon={Building2}
              title="Multi-Empresas"
              description="Gerencie múltiplas unidades de negócio em um só lugar. Dashboards e análises de IA separados para cada uma."
              delay={400}
            />
            <FeatureCard 
              icon={FileText}
              title="Relatórios em PDF"
              description="Exporte relatórios profissionais com um clique. Perfeito para reuniões, investidores ou análises detalhadas."
              delay={500}
            />
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <span 
                className="inline-block px-3 py-1 rounded-full text-sm mb-4"
                style={{ background: `${SECONDARY_COLOR}15`, color: SECONDARY_COLOR }}
              >
                Resultados
              </span>
              
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6">
                Pare de gerenciar no escuro. <span style={{ color: SECONDARY_COLOR }}>Veja tudo.</span>
              </h2>
              
              <p className="text-lg text-zinc-400 mb-8">
                Com o ALVO, você tem um Diretor Financeiro analisando seu negócio + um dashboard completo para acompanhar cada centavo.
              </p>

              <div className="space-y-6">
                {[
                  { icon: Brain, title: "IA que entende seu negócio", desc: "Análises personalizadas para seu setor: academia, restaurante, clínica, varejo..." },
                  { icon: AlertTriangle, title: "Nunca mais seja pego de surpresa", desc: "A IA detecta problemas antes que virem crises financeiras" },
                  { icon: Lightbulb, title: "Descubra dinheiro escondido", desc: "Oportunidades que você está deixando na mesa, quantificadas em R$" },
                  { icon: Target, title: "Plano de ação prático", desc: "Não apenas diagnóstico, mas COMO resolver, passo a passo" }
                ].map((item, i) => (
                  <div key={item.title} className="flex items-start gap-4">
                    <div 
                      className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ background: `${SECONDARY_COLOR}15` }}
                    >
                      <item.icon className="w-6 h-6" style={{ color: SECONDARY_COLOR }} />
                    </div>
                    <div>
                      <h4 className="font-semibold text-white mb-1">{item.title}</h4>
                      <p className="text-zinc-400">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 gap-4">
              <div className="p-6 rounded-2xl bg-zinc-900/50 border border-white/5 text-center">
                <p className="text-4xl sm:text-5xl font-bold mb-2" style={{ color: PRIMARY_COLOR }}>
                  <AnimatedCounter end={500} suffix="+" />
                </p>
                <p className="text-zinc-400">Empresários ativos</p>
              </div>
              <div className="p-6 rounded-2xl bg-zinc-900/50 border border-white/5 text-center">
                <p className="text-4xl sm:text-5xl font-bold mb-2" style={{ color: SECONDARY_COLOR }}>
                  <AnimatedCounter end={14} />
                </p>
                <p className="text-zinc-400">Setores atendidos</p>
              </div>
              <div className="p-6 rounded-2xl bg-zinc-900/50 border border-white/5 text-center">
                <p className="text-4xl sm:text-5xl font-bold mb-2" style={{ color: PRIMARY_COLOR }}>
                  <AnimatedCounter end={98} suffix="%" />
                </p>
                <p className="text-zinc-400">Satisfação</p>
              </div>
              <div className="p-6 rounded-2xl bg-zinc-900/50 border border-white/5 text-center">
                <p className="text-4xl sm:text-5xl font-bold mb-2" style={{ color: SECONDARY_COLOR }}>
                  R$
                </p>
                <p className="text-zinc-400">Economia identificada</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 lg:py-32 bg-zinc-900/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span 
              className="inline-block px-3 py-1 rounded-full text-sm mb-4"
              style={{ background: `${PRIMARY_COLOR}15`, color: PRIMARY_COLOR }}
            >
              Depoimentos
            </span>
            
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Quem usa, <span style={{ color: PRIMARY_COLOR }}>recomenda</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <TestimonialCard 
              name="Marcos Silva"
              role="Dono"
              company="Academia FitPro"
              text="A IA identificou que minha folha estava acima do benchmark. Ajustei e economizei R$ 4.000/mês. O melhor investimento que fiz."
            />
            <TestimonialCard 
              name="Renata Costa"
              role="Empresária"
              company="Restaurante Sabor"
              text="Finalmente entendo meus números. A análise mensal me mostrou oportunidades que nunca tinha visto. Faturamento subiu 22%."
            />
            <TestimonialCard 
              name="Carlos Mendes"
              role="CEO"
              company="Clínica Odonto+"
              text="Gerencio 3 unidades com o Alvo. A IA gera análises personalizadas para cada uma. É como ter um CFO dedicado."
            />
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 lg:py-32 relative">
        <div 
          className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full blur-[150px] opacity-10"
          style={{ background: GRADIENT }}
        />
        
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <span 
              className="inline-block px-3 py-1 rounded-full text-sm mb-4"
              style={{ background: `${PRIMARY_COLOR}15`, color: PRIMARY_COLOR }}
            >
              Planos
            </span>
            
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
              Invista em ter um <span style={{ color: PRIMARY_COLOR }}>Diretor Financeiro</span>
            </h2>
            
            <p className="text-lg text-zinc-400">
              Por menos que um café por dia, tenha IA analisando seu negócio.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {/* Monthly Plan */}
            <div className="p-8 rounded-2xl bg-zinc-900/50 border border-white/10 relative">
              <div 
                className="absolute top-4 right-4 px-3 py-1 rounded-full text-xs font-bold"
                style={{ background: SECONDARY_COLOR, color: '#000' }}
              >
                RECORRENTE
              </div>
              
              <h3 className="text-2xl font-bold mb-2">Mensal</h3>
              <p className="text-zinc-400 mb-6">Assinatura recorrente</p>
              
              <div className="mb-6">
                <span className="text-5xl font-bold">R$ 59</span>
                <span className="text-2xl font-bold text-zinc-400">,90</span>
                <span className="text-zinc-400">/mês</span>
              </div>

              <ul className="space-y-3 mb-8">
                {[
                  "Diretor Financeiro com IA",
                  "Dashboard inteligente completo",
                  "Análises ilimitadas",
                  "Multi-empresas",
                  "Relatórios em PDF",
                  "Cancele quando quiser"
                ].map((feature, i) => (
                  <li key={feature} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-[#22C55E] flex-shrink-0" />
                    <span className="text-zinc-300">{feature}</span>
                  </li>
                ))}
              </ul>

              <Button
                onClick={() => navigate('/login?tab=register')}
                variant="outline"
                className="w-full h-12 border-zinc-600 text-white hover:bg-white/5"
                data-testid="pricing-monthly-btn"
              >
                Assinar Agora
              </Button>
            </div>

            {/* Annual Plan - Highlighted */}
            <div 
              className="p-8 rounded-2xl border-2 relative overflow-hidden"
              style={{ 
                background: 'linear-gradient(180deg, rgba(168, 201, 74, 0.1) 0%, rgba(30, 30, 30, 0.5) 100%)',
                borderColor: PRIMARY_COLOR
              }}
            >
              {/* Popular badge */}
              <div 
                className="absolute top-4 right-4 px-3 py-1 rounded-full text-xs font-bold"
                style={{ background: PRIMARY_COLOR, color: '#000' }}
              >
                MAIS ECONOMIA
              </div>

              <h3 className="text-2xl font-bold mb-2">Anual</h3>
              <p className="text-zinc-400 mb-6">Economia de 25% • Parcele em até 12x</p>
              
              <div className="mb-2">
                <span className="text-lg text-zinc-500 line-through mr-2">R$ 718,80</span>
                <span className="text-5xl font-bold" style={{ color: PRIMARY_COLOR }}>R$ 538</span>
                <span className="text-2xl font-bold" style={{ color: PRIMARY_COLOR }}>,80</span>
              </div>
              <p className="text-sm text-zinc-400 mb-6">
                ou <span className="text-white font-semibold">12x de R$ 44,90</span> no cartão
              </p>

              <ul className="space-y-3 mb-8">
                {[
                  "Tudo do plano mensal",
                  "Prioridade no suporte",
                  "Empresas ilimitadas",
                  "Parcele em até 12x",
                  "Economize R$ 180/ano"
                ].map((feature, i) => (
                  <li key={feature} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-[#22C55E] flex-shrink-0" />
                    <span className="text-zinc-300">{feature}</span>
                  </li>
                ))}
              </ul>

              <Button
                onClick={() => navigate('/login?tab=register')}
                className="w-full h-12 text-black font-bold"
                style={{ background: GRADIENT }}
                data-testid="pricing-annual-btn"
              >
                Economizar 25%
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
          </div>

          {/* Guarantee */}
          <div className="mt-12 text-center">
            <div className="inline-flex items-center gap-3 px-6 py-3 rounded-full bg-zinc-900/50 border border-white/5">
              <Shield className="w-5 h-5 text-[#22C55E]" />
              <span className="text-zinc-300">Garantia de 7 dias ou seu dinheiro de volta</span>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20 lg:py-32 bg-zinc-900/30">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span 
              className="inline-block px-3 py-1 rounded-full text-sm mb-4"
              style={{ background: `${SECONDARY_COLOR}15`, color: SECONDARY_COLOR }}
            >
              FAQ
            </span>
            
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Dúvidas frequentes
            </h2>
          </div>

          <div className="divide-y divide-white/10">
            {faqs.map((faq, index) => (
              <FAQItem
                key={faq.question}
                question={faq.question}
                answer={faq.answer}
                isOpen={openFAQ === index}
                onClick={() => setOpenFAQ(openFAQ === index ? null : index)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 lg:py-32 relative overflow-hidden">
        {/* Background */}
        <div 
          className="absolute inset-0"
          style={{ 
            background: `linear-gradient(180deg, transparent 0%, ${PRIMARY_COLOR}10 50%, transparent 100%)`
          }}
        />
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl mb-8" style={{ background: GRADIENT }}>
            <Brain className="w-10 h-10 text-black" />
          </div>
          
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6">
            Sua empresa merece um <span style={{ color: PRIMARY_COLOR }}>Diretor Financeiro</span>
          </h2>
          
          <p className="text-xl text-zinc-400 mb-10 max-w-2xl mx-auto">
            Junte-se a mais de 500 empresários que já têm IA analisando seus números e encontrando oportunidades de lucro.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => navigate('/login?tab=register')}
              className="h-14 px-10 text-lg text-black font-bold"
              style={{ background: GRADIENT }}
              data-testid="final-cta-btn"
            >
              Conhecer o ALVO
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>

          <p className="text-sm text-zinc-500 mt-6">
            Sem compromisso. Cancele quando quiser. Garantia de 7 dias.
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <AlvoLogo size={32} />
              <span className="font-bold">ALVO</span>
              <span className="text-zinc-500">Diretor Financeiro com I.A.</span>
            </div>
            
            <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6 text-sm text-zinc-500">
              <div className="flex items-center gap-5">
                <button
                  onClick={() => navigate('/privacidade')}
                  className="hover:text-white transition-colors"
                  data-testid="footer-privacy-link"
                >
                  Privacidade
                </button>
                <button
                  onClick={() => navigate('/termos')}
                  className="hover:text-white transition-colors"
                  data-testid="footer-terms-link"
                >
                  Termos de Uso
                </button>
              </div>
              <a 
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-[#25D366] hover:text-[#20bd5a] transition-colors"
                data-testid="footer-whatsapp-link"
              >
                <Phone className="w-4 h-4" />
                <span>Tire suas dúvidas: (84) 99968-3171</span>
              </a>
              <span>&copy; {new Date().getFullYear()} Alvo. Todos os direitos reservados.</span>
            </div>
          </div>
        </div>
      </footer>

      {/* Floating WhatsApp Button */}
      <a
        href={WHATSAPP_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 flex items-center gap-3 px-5 py-3 rounded-full bg-[#25D366] text-white font-semibold shadow-lg hover:bg-[#20bd5a] hover:scale-105 transition-all duration-300"
        data-testid="floating-whatsapp-btn"
      >
        <svg viewBox="0 0 24 24" className="w-6 h-6 fill-current">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
        <span className="hidden sm:inline">Tire suas dúvidas com a Alvo</span>
      </a>

      {/* CSS Animations */}
      <style>{`
        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes float-slow {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        
        @keyframes float-reverse {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(10px); }
        }
        
        .animate-fade-in-up {
          animation: fade-in-up 0.6s ease-out forwards;
        }
        
        .animate-float-slow {
          animation: float-slow 4s ease-in-out infinite;
        }
        
        .animate-float-reverse {
          animation: float-reverse 4s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default LandingPage;
